import { useGetSettingsFooter } from '@agos/api-client/platform'
import { safeLink } from '@agos/shared'
import { useMemo } from 'react'

import { openingHoursOf, type OpeningHours } from './opening'

/**
 * The restaurant's own details — name, phone, address, socials.
 *
 * **This is a hook over the query, not a zustand store**, and the difference is the point.
 * `docs/DECISIONS.md` lists the restaurant details among the customer app's zustand state, copying
 * legacy's five stores; legacy keeps them in a store because it has no query library. AGOS has one, and
 * FRONTEND.md §3 gives it everything the server owns. A zustand copy of `GET /settings/footer` would be
 * a second cache with its own staleness, invalidation and bugs, for data nobody edits from this app.
 *
 * The two stores that remain zustand — the cart and the branch — hold facts the **client** owns, which
 * is the line the decision was really drawing.
 *
 * Every link is passed through `safeLink`: these are admin-authored fields, and a `javascript:` URL in
 * one of them would run in the customer's session.
 */

export interface RestaurantInfo {
  name: string
  phone: string | null
  address: string | null
  logoUrl: string | null
  facebookUrl: string | null
  instagramUrl: string | null
  tiktokUrl: string | null
  googleReviewsUrl: string | null
  mapEmbedUrl: string | null
  /** The same payload the menu reads for its open/closed state — the footer prints the week. */
  openingHours: OpeningHours | null
}

export function restaurantInfoOf(payload: unknown): RestaurantInfo {
  const row = (typeof payload === 'object' && payload !== null ? payload : {}) as Record<string, unknown>
  const text = (key: string): string | null => {
    const value = row[key]
    return typeof value === 'string' && value.trim() !== '' ? value.trim() : null
  }
  return {
    name: text('restaurantName') ?? '',
    phone: text('phone'),
    address: text('address'),
    logoUrl: safeLink(row.logoUrl),
    facebookUrl: safeLink(row.facebookUrl),
    instagramUrl: safeLink(row.instagramUrl),
    tiktokUrl: safeLink(row.tiktokUrl),
    googleReviewsUrl: safeLink(row.googleReviewsUrl),
    mapEmbedUrl: safeLink(row.mapEmbedUrl),
    openingHours: openingHoursOf(row.openingHours),
  }
}

export function useRestaurantInfo(): RestaurantInfo {
  const { data } = useGetSettingsFooter()
  return useMemo(() => restaurantInfoOf(data), [data])
}
